// Bounded observation of a queued Task-thread append. The public task-thread hook owns admission and view
// state; this helper owns only the eventual Task snapshot polling needed while no run id exists yet.

import { useEffect } from 'react';
import { useSurfaceApi } from './SurfaceApiContext.tsx';
import type { TaskDetailProjection } from './know-projections.ts';

const QUEUED_POLL_EXHAUSTED_NOTE =
  'Your update is still queued on this task. Reopen the task later to check its progress.';
/** One read per second for at most five minutes: visible eventual hand-off without an unbounded poller. */
const QUEUED_POLL_INTERVAL_MS = 1_000;
const MAX_QUEUED_POLL_ATTEMPTS = 300;

export interface QueuedRunObservation {
  readonly baselineRunIds: readonly string[];
  readonly message: string;
  readonly attempts: number;
}

interface MutableValue<T> {
  current: T;
}

interface QueuedRunStash {
  queuedRun: QueuedRunObservation | null;
}

interface QueuedRunObserverInput {
  readonly taskId: string;
  readonly detail: TaskDetailProjection;
  readonly reload: () => void;
  readonly composerStash: MutableValue<QueuedRunStash>;
}

interface QueueNoteState {
  readonly note: string | null;
  readonly setNote: (value: string | null) => void;
}

/** Find the run created for one queued append: it must be new since admission and carry that exact human turn. */
function findObservedQueuedRun(
  detail: TaskDetailProjection,
  observation: QueuedRunObservation,
): TaskDetailProjection['runs'][number] | undefined {
  const baseline = new Set(observation.baselineRunIds);
  return detail.runs.find(
    (run) =>
      !baseline.has(run.id) &&
      run.turns.some((turn) => turn.author === 'human' && turn.text === observation.message),
  );
}

/** Start the bounded Task-read loop and return its idempotent teardown. */
function beginQueuedRunPolling(
  api: ReturnType<typeof useSurfaceApi>,
  input: QueuedRunObserverInput,
  mounted: MutableValue<boolean>,
  setNote: QueueNoteState['setNote'],
): () => void {
  const control: { cancelled: boolean; timer?: ReturnType<typeof setTimeout> } = {
    cancelled: false,
  };
  const schedule = (): void => {
    control.timer = setTimeout(() => void poll(), QUEUED_POLL_INTERVAL_MS);
  };
  const poll = async (): Promise<void> => {
    if (control.cancelled || !mounted.current) return;
    const current = input.composerStash.current.queuedRun;
    if (current === null) return;
    if (current.attempts >= MAX_QUEUED_POLL_ATTEMPTS) {
      input.composerStash.current.queuedRun = null;
      setNote(QUEUED_POLL_EXHAUSTED_NOTE);
      return;
    }
    input.composerStash.current.queuedRun = { ...current, attempts: current.attempts + 1 };
    try {
      const snapshot = await api.getTaskDetail(input.taskId);
      if (control.cancelled || !mounted.current) return;
      const active = input.composerStash.current.queuedRun;
      if (active === null) return;
      if (findObservedQueuedRun(snapshot, active) !== undefined) {
        input.composerStash.current.queuedRun = null;
        setNote(null);
        input.reload();
        return;
      }
    } catch {
      // Best-effort observation: keep the visible queued note and retry within the hard bound.
    }
    if (!control.cancelled && mounted.current) schedule();
  };
  void poll();
  return () => {
    control.cancelled = true;
    if (control.timer !== undefined) clearTimeout(control.timer);
  };
}

/**
 * A queued admission has no run id yet. Poll the same Task-scoped read with a hard attempt bound until the
 * append becomes its own running/terminal run, then perform one authoritative SurfaceFrame reload.
 */
export function useQueuedRunObserver(
  input: QueuedRunObserverInput,
  mounted: MutableValue<boolean>,
  composer: QueueNoteState,
): void {
  const api = useSurfaceApi();
  const note = composer.note;
  const setNote = composer.setNote;
  useEffect(() => {
    const initial = input.composerStash.current.queuedRun;
    if (initial === null) return;

    const visible = findObservedQueuedRun(input.detail, initial);
    if (visible !== undefined) {
      input.composerStash.current.queuedRun = null;
      setNote(null);
      return;
    }
    return beginQueuedRunPolling(api, input, mounted, setNote);
  }, [api, input.composerStash, input.detail, input.reload, input.taskId, mounted, note, setNote]);
}
